// `/admin/products/:productId` also takes `sku=<artNumber>`, so a product can
// be opened, linked and shared by the SKU people know instead of its id.
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useParams } from 'react-router-dom';
import { getAdminProducts } from '@/api/admin';
import { Card, LoadingState } from '@/components/ui';
import { AdminProductConfigPage } from './AdminProductConfigPage';
import { ADMIN_PRODUCT_PARAMS } from './admin-list-params';

const SKU_PREFIX = 'sku=';

/** The SKU a `sku=<artNumber>` segment names, null for a plain product id. */
export function skuFromProductParam(param: string): string | null {
  return param.startsWith(SKU_PREFIX)
    ? param.slice(SKU_PREFIX.length).trim() || null
    : null;
}

export function AdminProductRoute() {
  const { productId = '' } = useParams<{ productId: string }>();
  const sku = skuFromProductParam(productId);
  return sku === null ? <AdminProductConfigPage /> : <ProductBySku sku={sku} />;
}

function ProductBySku({ sku }: { sku: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin', 'product-by-sku', sku],
    // The search matches parts of names and SKUs; only an exact SKU counts.
    queryFn: () => getAdminProducts({ search: sku, limit: 100 }),
  });

  if (isLoading) return <LoadingState label="Produkt wird gesucht…" />;

  const matches = (data?.data ?? []).filter(
    (product) => product.artNumber?.toLowerCase() === sku.toLowerCase()
  );
  if (matches.length === 1) {
    return <Navigate to={`/admin/products/${matches[0]!.id}`} replace />;
  }
  // Several products can share a SKU: the list shows them side by side.
  if (matches.length > 1) {
    const search = new URLSearchParams({ [ADMIN_PRODUCT_PARAMS.search]: sku });
    return <Navigate to={`/admin/products?${search.toString()}`} replace />;
  }

  return (
    <Card className="mx-auto w-full max-w-7xl">
      <p className="text-sm text-danger">
        {isError
          ? 'Produktsuche fehlgeschlagen.'
          : `Kein Produkt mit der SKU ${sku}.`}
      </p>
      <Link
        to="/admin/products"
        className="mt-3 inline-block text-sm font-medium text-ink-2 transition-colors hover:text-ink"
      >
        ← Back to products
      </Link>
    </Card>
  );
}
